import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Settings as SettingsIcon, AlertTriangle, Search, ExternalLink } from 'lucide-react';
import { Settings } from '@/components/Settings';
import { useGoogleApi, CSE_ID_BOOKING, CSE_ID_GENERAL, SearchResult } from '@/hooks/useGoogleApi';

interface ImageInfo {
  domain: string;
  url: string;
  dataSrc?: string;
  srcset?: string;
  width: number | string;
  height: number | string;
  alt: string;
  className: string;
}

interface SearchState {
  input: string;
  inputType: 'url' | 'terms';
  searchMode: 'general' | 'booking';
}

export default function BookingTest() {
  const [images, setImages] = useState<ImageInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedImages, setExtractedImages] = useState<string[]>([]);
  const [extensionStatus, setExtensionStatus] = useState('Aguardando...');
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const { isConfigured, searchPages } = useGoogleApi();
  const { toast } = useToast();

  // Estado para o novo sistema de busca
  const [search, setSearch] = useState<SearchState>({
    input: '',
    inputType: 'terms',
    searchMode: 'booking'
  });

  // Função para detectar se é URL
  const isValidUrl = (string: string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Handler para mudança no input
  const handleInputChange = (value: string) => {
    const inputType = isValidUrl(value) ? 'url' : 'terms';
    setSearch(prev => ({
      ...prev,
      input: value,
      inputType
    }));
  };

  // Handler para mudança no modo de busca
  const handleModeToggle = (checked: boolean) => {
    setSearch(prev => ({
      ...prev,
      searchMode: checked ? 'booking' : 'general'
    }));
  };

  // Listener para mensagens do script do Booking
  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verifica se a página está montada
      const isValidSource = event.data.source === 'TRAVEL_MAGIC_EXTENSION';
      const isValidSession = !event.data.sessionId || event.data.sessionId === currentSessionId;
      
      if (isValidSource && isValidSession) {
        if (event.data.type === 'TEST_RESULT' && event.data.data?.allUrls) {
          setImages(event.data.data.allUrls || []);
          setIsExtracting(false);
          
          toast({
            title: 'Extração Concluída',
            description: `Foram extraídas ${event.data.urls?.length || 0} imagens.`,
          });
        }

        if (event.data?.type === 'EXTENSION_TEST') {
          setExtensionStatus('Extensão detectada!');
          toast({
            title: 'Extensão Detectada',
            description: 'A extensão está instalada e funcionando corretamente.',
          });
        }

        if (event.data?.type === 'EXTRACTION_RESULT') {
          setIsExtracting(false);
          setExtractedImages(event.data.urls || []);
          toast({
            title: 'Extração Concluída',
            description: `Foram extraídas ${event.data.urls?.length || 0} imagens.`,
          });
        }
      }
    };

    const mounted = true;
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [toast, currentSessionId]);

  // Função para gerar um ID de sessão único
  const generateSessionId = () => {
    return Date.now().toString() + '-' + Math.random().toString(36).substring(2, 9);
  };

  // Handler para seleção de URL dos resultados
  const handleSelectUrl = (url: string) => {
    setSearch(prev => ({
      ...prev,
      input: url,
      inputType: 'url'
    }));
    setSearchResults([]); // Limpa os resultados após seleção
  };

  // Handler para submissão do formulário
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!search.input.trim()) {
      toast({
        title: "Campo vazio",
        description: "Por favor, insira uma URL ou termo de busca.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    setImages([]); // Limpa resultados anteriores
    
    try {
      if (search.inputType === 'url') {
        // Usa o fluxo existente para URLs
        const newSessionId = generateSessionId();
        setCurrentSessionId(newSessionId);
        setIsExtracting(true);
        setExtractedImages([]);
        setSearchResults([]); // Limpa resultados de busca ao extrair

        const url = new URL(search.input);
        url.searchParams.append('sessionId', newSessionId);
        
        const newTab = window.open(url.toString(), '_blank');
        
        let attempts = 0;
        const focusInterval = setInterval(() => {
          window.focus();
          attempts++;
          if (attempts >= 5) clearInterval(focusInterval);
        }, 500);

        setTimeout(() => clearInterval(focusInterval), 3000);
      } else {
        // Realiza a busca de páginas
        const cseId = search.searchMode === 'booking' ? CSE_ID_BOOKING : CSE_ID_GENERAL;
        const results = await searchPages(search.input, cseId);
        
        if (results.length === 0) {
          toast({
            title: "Nenhum resultado",
            description: "Não foram encontradas páginas para esta busca.",
            variant: "default"
          });
          return;
        }

        setSearchResults(results);
        
        toast({
          title: "Busca concluída",
          description: `Foram encontradas ${results.length} páginas.`,
        });
      }
    } catch (error) {
      console.error('Erro na busca:', error);
      toast({
        title: "Erro na busca",
        description: error instanceof Error ? error.message : "Erro ao processar a busca",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setIsExtracting(false);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Extrator de Imagens</h1>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setShowSettings(true)}
        >
          <SettingsIcon className="h-4 w-4 mr-2" />
          Configurações
        </Button>
      </div>
      
      {!isConfigured && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                A API do Google não está configurada. 
                <button 
                  className="font-medium underline ml-1"
                  onClick={() => setShowSettings(true)}
                >
                  Configurar agora
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <div className="bg-muted/50 rounded-lg p-4">
          <h2 className="font-medium mb-2">Status da Extensão:</h2>
          <p className="text-sm text-muted-foreground">{extensionStatus}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="searchInput">URL ou Termo de Busca</Label>
            <div className="flex gap-3">
              <Input
                id="searchInput"
                value={search.input}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder="Digite a busca ou cole uma URL"
                className="flex-1"
              />
              <Button 
                type="submit"
                disabled={!search.input || loading || isExtracting}
              >
                {loading || isExtracting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {search.inputType === 'url' ? 'Extraindo...' : 'Buscando...'}
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    {search.inputType === 'url' ? 'Extrair Imagens' : 'Buscar'}
                  </>
                )}
              </Button>
            </div>
            
            {search.inputType === 'terms' && (
              <div className="flex items-center space-x-2 mt-2">
                <Switch
                  id="search-mode"
                  checked={search.searchMode === 'booking'}
                  onCheckedChange={handleModeToggle}
                />
                <Label htmlFor="search-mode">
                  {search.searchMode === 'booking' ? 'Busca Booking.com' : 'Busca Geral'}
                </Label>
              </div>
            )}
          </div>
        </form>

        {/* Resultados da Busca */}
        {searchResults.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Resultados da Busca</h2>
            <div className="grid grid-cols-1 gap-4">
              {searchResults.map((result, index) => (
                <div 
                  key={index} 
                  className="border p-4 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleSelectUrl(result.url)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium">{result.title}</h3>
                      <p className="text-sm text-gray-600">{result.description}</p>
                      <p className="text-sm text-blue-600 mt-1">{result.url}</p>
                    </div>
                    <ExternalLink className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Grid de Imagens */}
        {images.length > 0 && (
          <div className="mb-4">
            <h2 className="text-xl font-semibold mb-2">URLs Encontradas ({images.length})</h2>
            <div className="grid grid-cols-1 gap-4">
              {images.map((img, index) => (
                <div key={index} className="border p-4 rounded-lg">
                  <div className="flex gap-4">
                    <div className="w-40 h-40 relative">
                      <img 
                        src={img.url} 
                        alt={img.alt || 'Imagem do hotel'} 
                        className="object-cover w-full h-full rounded"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold">Domínio: {img.domain}</p>
                      <p className="text-sm break-all">URL: {img.url}</p>
                      {img.dataSrc && (
                        <p className="text-sm break-all">Data-src: {img.dataSrc}</p>
                      )}
                      {img.srcset && (
                        <p className="text-sm break-all">Srcset: {img.srcset}</p>
                      )}
                      <p className="text-sm">Dimensões: {img.width} x {img.height}</p>
                      {img.className && (
                        <p className="text-sm">Classe: {img.className}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal de Configurações */}
      <Settings open={showSettings} setOpen={setShowSettings} />
    </div>
  );
} 